import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetLeavesQuery, useGetLeaveBalanceQuery, useGetLeavesForCfmQuery } from "../../slices/leaveApiSlice";
import { useSelector } from "react-redux";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function LeaveRecords() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");

  // REMOVE default date range - set to null to show ALL dates
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Leave type options for CustomSelect
  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];

  // Status options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Approved", label: "Approved" },
    { value: "Pending", label: "Pending" },
    { value: "Rejected", label: "Rejected" },
    { value: "Forwarded", label: "Forwarded" },
  ];

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);

  // Get user info from Redux store
  const { userInfo } = useSelector((state) => state.auth);
  const userId = userInfo?.userId || userInfo?.id || userInfo?._id || '';

  console.log("CfmLeaveRecords - userInfo:", userInfo);
  console.log("CfmLeaveRecords - userId for query:", userId);

  // Use the specific CFM leave API hook to fetch data
  // This hook is better for CFM role as it handles academy scoping and role filtering
  const {
    data: leaveData = [],
    isLoading,
    error,
    refetch
  } = useGetLeavesForCfmQuery({ cfmId: userId }, {
    skip: !userId, // Skip query if no userId
  });

  // Fetch leave balance for current user
  const {
    data: leaveBalanceData,
    isLoading: isLoadingBalance,
    error: balanceError,
    refetch: refetchBalance
  } = useGetLeaveBalanceQuery();

  // Calculate days based on duration
  const calculateDays = (startDate, endDate, duration) => {
    if (duration === "HALF_DAY") return 0.5;
    if (duration === "FULL_DAY") return 1;

    // For multi-day, calculate actual days
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }

    return 1;
  };

  // Calculate used leave days from approved leave records
  const calculateUsedLeaveDays = useMemo(() => {
    if (!leaveData || !Array.isArray(leaveData) || leaveData.length === 0) {
      return { earnedUsed: 0, casualUsed: 0 };
    }

    let earnedUsed = 0;
    let casualUsed = 0;

    leaveData.forEach(leave => {
      // Only count approved leaves
      if (leave.status === "APPROVED" || leave.status === "Approved") {
        const days = calculateDays(leave.startDate, leave.endDate, leave.duration);

        if (leave.leaveType === "EARN") {
          earnedUsed += days;
        } else if (leave.leaveType === "CASUAL") {
          casualUsed += days;
        }
        // Manual leaves don't affect balance
      }
    });

    return { earnedUsed, casualUsed };
  }, [leaveData]);

  // Process leave balance data from API
  const processLeaveBalance = () => {
    if (!leaveBalanceData) {
      return {
        earned: { total: 0, used: 0, remaining: 0 },
        casual: { total: 0, used: 0, remaining: 0 }
      };
    }

    const earnedTotal = leaveBalanceData.total_earned_leave_days ||
      leaveBalanceData.totalEarnedLeaveDays || 0;

    const casualTotal = leaveBalanceData.total_casual_leave_days ||
      leaveBalanceData.totalCasualLeaveDays || 0;

    const earnedRemaining = leaveBalanceData.earned_leave_balance_remaining ||
      leaveBalanceData.earnedLeaveBalanceRemaining || 0;

    const casualRemaining = leaveBalanceData.casual_leave_balance_remaining ||
      leaveBalanceData.casualLeaveBalanceRemaining || 0;

    const { earnedUsed, casualUsed } = calculateUsedLeaveDays;

    return {
      earned: {
        total: Math.max(0, earnedTotal),
        used: Math.max(0, earnedUsed),
        remaining: Math.max(0, earnedRemaining)
      },
      casual: {
        total: Math.max(0, casualTotal),
        used: Math.max(0, casualUsed),
        remaining: Math.max(0, casualRemaining)
      }
    };
  };

  const leaveBalances = processLeaveBalance();

  const handleBack = () => {
    navigate("/cfm/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
      case "Approved":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "REJECTED":
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "FORWARDED":
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (short) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      date.setHours(date.getHours() + 6);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return dateString;
    }
  };

  // Transform API data to match your frontend structure
  const transformLeaveData = (apiData) => {
    // If API returns no data or fails, use dummy data
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      console.log("No API data found");
      return [];
    }

    console.log("Total records from API:", apiData.length);

    // If API returns data, transform it and sort by submitted date (newest first)
    const transformedData = apiData.map((leave, index) => {
      console.log(`Processing leave ${index + 1}:`, {
        id: leave._id,
        startDate: leave.startDate,
        endDate: leave.endDate,
        totalDays: leave.totalDays,
        type: leave.leaveType,
        status: leave.status
      });

      // Use totalDays from API directly
      const days = leave.totalDays || calculateDays(leave.startDate, leave.endDate, leave.duration);
      const isSingleDay = days <= 1 ||
        leave.duration === "FULL_DAY" ||
        leave.duration === "HALF_DAY" ||
        (leave.startDate && leave.endDate && leave.startDate === leave.endDate);

      // Extract admin info from notes if available
      const adminInfo = extractAdminInfo(leave);

      return {
        id: leave._id || leave.id || `leave-${index}`,
        type: leave.leaveType === "CASUAL" ? "Casual Leave" :
          leave.leaveType === "EARN" ? "Earned Leave" :
            (leave.leaveType === "MANUAL" ? "Manual Leave" : leave.leaveType),
        reason: leave.reason || "No reason provided",
        days: days,
        startDate: leave.startDate,
        endDate: leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: leave.status || "PENDING",
        employee: leave.Email || userInfo?.email || "Unknown",
        position: "CFM",
        email: leave.Email || userInfo?.email || "",

        // Admin approval information
        adminNote: leave.adminNote || adminInfo.note || leave.cfmNote || "",
        adminName: leave.adminName || adminInfo.name || "Admin",
        adminEmail: leave.managerEmail || adminInfo.email || "",
        adminRole: leave.adminRole || "Admin",
        adminNoteDate: leave.adminNoteDate || adminInfo.date || leave.decisionAt || "",

        // Keep original data for details view
        originalEndDate: leave.endDate,
        isSingleDay: isSingleDay,
        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
      };
    });

    // Sort by submitted date (newest first)
    return transformedData.sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
  };

  // Extract admin info from workflow notes
  const extractAdminInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    // Look for admin notes in the workflow
    const adminNotes = leave.notes.filter(note =>
      note.actorRole === 'ADMIN' ||
      note.actorRole === 'Admin' ||
      note.actorRole === 'CFM' ||
      note.actorRole === 'Cfm' ||
      note.actorRole === 'SYSTEM_ADMIN'
    );

    if (adminNotes.length > 0) {
      const latestNote = adminNotes[adminNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "System Admin",
        email: latestNote.actorEmail || latestNote.actorId || "admin@system.com",
        date: latestNote.timestamp || ""
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  // SIMPLIFIED: Show ALL data without date filtering
  const filteredData = useMemo(() => {
    const transformed = transformLeaveData(leaveData);
    console.log("Showing ALL records:", transformed.length);

    // Apply only status and leave type filters (NO DATE FILTER)
    return transformed.filter(record => {
      const leaveTypeMatch = selectedLeaveType === "All" || record.type === selectedLeaveType;
      const statusMatch = selectedStatus === "All" ||
        record.status.toLowerCase() === selectedStatus.toLowerCase();

      return leaveTypeMatch && statusMatch;
    });
  }, [leaveData, selectedLeaveType, selectedStatus]);

  // Debug: Log data on load
  useEffect(() => {
    console.log("API Response - leaveData:", leaveData);
    console.log("Transformed data count:", transformLeaveData(leaveData).length);
    console.log("Filtered data count:", filteredData.length);
  }, [leaveData, filteredData]);

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
  };

  // Refresh data
  const handleRefresh = () => {
    refetch();
  };

  const LeaveBalanceCard = ({ title, balance, color = "blue", isLoading = false }) => {
    const colorClasses = {
      blue: {
        text: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        progress: "bg-blue-500"
      },
      purple: {
        text: "text-purple-600",
        bg: "bg-purple-50",
        border: "border-purple-200",
        progress: "bg-purple-500"
      },
      gray: {
        text: "text-gray-600",
        bg: "bg-gray-50",
        border: "border-gray-200",
        progress: "bg-gray-500"
      }
    };

    const currentColor = colorClasses[color];

    if (isLoading) {
      return (
        <div className="bg-[#FAFAFA] rounded-lg p-6 animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
          </div>
        </div>
      );
    }

    return (
      <div className={`bg-[#FAFAFA] rounded-lg p-4 sm:p-6`}>
        <h3 className="font-semibold text-gray-800 mb-3 sm:mb-4 text-sm sm:text-base">{title}</h3>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 text-sm sm:text-base">Total Entitled:</span>
            <span className="font-medium text-sm sm:text-base">{balance.total} days</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-600 text-sm sm:text-base">Used:</span>
            <span className="font-medium text-red-600 text-sm sm:text-base">
              - {Math.max(0, balance.total - balance.remaining)} days
            </span>
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
            <span className="text-gray-800 font-semibold text-sm sm:text-base">Remaining</span>
            <div className="text-right">
              <div className={`text-sm ${currentColor.text} font-bold`}>
                {balance.remaining} days
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Remaining: {balance.remaining} days</span>
              <span>Used: {Math.max(0, balance.total - balance.remaining)} days</span>
            </div>
            <div className="flex h-2 sm:h-3 rounded-full overflow-hidden border border-gray-300">
              <div
                className={`${currentColor.progress} transition-all duration-300`}
                style={{ width: `${(balance.remaining / balance.total) * 100}%` }}
              ></div>
              <div
                className="bg-gray-200 transition-all duration-300"
                style={{ width: `${((balance.total - balance.remaining) / balance.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8 max-w-10xl mx-auto">
        <div className="flex items-center space-x-1 mb-6">
          <button onClick={handleBack} className="w-9 h-9 flex items-center justify-center bg-gray-50 border-2 border-black rounded-full">
            <ChevronLeft size={32} strokeWidth={2} className="text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">My Leave Records</h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 max-w-10xl mx-auto">
        <div className="flex items-center space-x-1 mb-6">
          <button onClick={handleBack} className="w-9 h-9 flex items-center justify-center bg-gray-50 border-2 border-black rounded-full">
            <ChevronLeft size={32} strokeWidth={2} className="text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">My Leave Records</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-red-800 font-semibold mb-2">Error loading leave records</h3>
          <p className="text-red-600 mb-4">{error?.data?.message || "Failed to fetch leave data"}</p>
          <button
            onClick={handleRefresh}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-1">
          <button
            aria-label="Back"
            onClick={handleBack}
            className="md:flex w-9 h-9 items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all rounded-full"
          >
            <ChevronLeft
              size={24}
              strokeWidth={2}
              className="text-gray-700"
            />
          </button>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">My Leave Records</h1>
          </div>
        </div>
      </div>

      {/* Leave Balance Cards at Top */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <LeaveBalanceCard
          title="Earned Leave Balance"
          balance={leaveBalances.earned}
          color="blue"
        />
        <LeaveBalanceCard
          title="Casual Leave Balance"
          balance={leaveBalances.casual}
          color="purple"
        />
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex flex-col items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">Records</h2>
              <p className="text-sm text-zinc-600 mt-1">
                Showing {filteredData.length} of {transformLeaveData(leaveData).length} records
              </p>
            </div>

            <div className="w-full mt-3">
              <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-full sm:w-auto min-w-0">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <CustomSelect
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    placeholder="All"
                    minWidth={140}
                  />
                </div>
                <div className="w-full sm:w-auto min-w-0">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Leave Type</label>
                  <CustomSelect
                    options={leaveTypeOptions}
                    value={selectedLeaveType}
                    onChange={setSelectedLeaveType}
                    placeholder="All"
                    minWidth={140}
                  />
                </div>
                <div className="w-full sm:w-auto min-w-0">
                  <button
                    onClick={() => {
                      setSelectedStatus("All");
                      setSelectedLeaveType("All");
                      setStartDate(null);
                      setEndDate(null);
                    }}
                    className="mt-6 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Show All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TYPE
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DAYS
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  SUBMITTED ON
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  FROM
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TO
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  STATUS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {filteredData.length > 0 ? (
                filteredData.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(record)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      {record.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDateTime(record.submittedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {record.isSingleDay ? formatDate(record.startDate) : formatDate(record.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-zinc-500">
                    {transformLeaveData(leaveData).length === 0
                      ? "No leave records found for your account"
                      : "No leave records match your filters"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leave Details Overlay */}
      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-3 sm:p-4">
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex-shrink-0">
                    <span className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}>
                      {selectedLeave.status}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{selectedLeave.email}</h3>
                      <p className="text-xs sm:text-sm text-gray-600">{selectedLeave.position}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">{selectedLeave.type}</h3>
              </div>

              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">Reason:</h4>
                <div className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <p className="text-sm text-blue-600 mb-2">
                      Half Day - {selectedLeave.halfDaySession === 'MORNING' ? 'Morning' : 'Afternoon'} Session
                    </p>
                  )}
                  {selectedLeave.reason}
                </div>
              </div>

              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Submitted On:</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>

                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Leave Date:</span>
                    <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">From:</span>
                      <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">To:</span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Days</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days} {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {(selectedLeave.status === "Approved" || selectedLeave.status === "APPROVED" ||
                selectedLeave.status === "Rejected" || selectedLeave.status === "REJECTED") && (
                  <div className="bg-gray-50 rounded-lg">
                    {selectedLeave.adminNote ? (
                      <div className="p-2 sm:p-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                              {selectedLeave.adminEmail?.charAt(0)?.toUpperCase() || 'A'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              {selectedLeave.adminEmail}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600">Admin</p>
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-gray-700 text-sm sm:text-base">{selectedLeave.adminNote}</p>
                          {selectedLeave.adminNoteDate && (
                            <p className="text-xs text-gray-500 mt-1">
                              Note added on: {formatDateTime(selectedLeave.adminNoteDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 text-center">
                        {selectedLeave.status === "Approved" || selectedLeave.status === "APPROVED" ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-semibold text-green-800 text-sm sm:text-base mb-2">
                              ✅ Approved by Admin
                            </h4>
                            <p className="text-green-700 text-sm">
                              Your leave request has been approved by the system administrator.
                            </p>
                          </div>
                        ) : (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="font-semibold text-red-800 text-sm sm:text-base mb-2">
                              ❌ Rejected by Admin
                            </h4>
                            <p className="text-red-700 text-sm">
                              Your leave request has been rejected by the system administrator.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}